import { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  AttendanceStatusEnum,
  AttendenceStatusLabel,
} from "../../Constants/Enums/Attendance/AttendanceStatusEnum";

const useAttendance = (service) => {
  const [attendanceData, setAttendanceData] = useState({
    records: [],
    summary: null,
    employees: [],
    departments: [],
  });

  const [loading, setLoading] = useState({
    department: false,
    employees: false,
    departments: false,
  });

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [barChartData, setBarChartData] = useState(null);
  const [pieChartData, setPieChartData] = useState(null);
  const [options, setOptions] = useState({
    responsive: true,
    plugins: { legend: { position: "top" } },
  });
  // Status mapping function using your enum
  const getStatusFromCode = (statusCode) => {
    if (statusCode === null || statusCode === undefined) return "unknown";
    const code = parseInt(statusCode);

    switch (code) {
      case AttendanceStatusEnum.Present:
        return "present";
      case AttendanceStatusEnum.Absent:
        return "absent";
      case AttendanceStatusEnum.OnLeave:
        return "onLeave";
      case AttendanceStatusEnum.Incomplete:
        return "incomplete";
      case AttendanceStatusEnum.HalfDay:
        return "halfDay";
      case AttendanceStatusEnum.ShortTime:
        return "shortTime";
      case AttendanceStatusEnum.Weekend:
        return "weekend";
      case AttendanceStatusEnum.Holiday:
        return "holiday";
      case AttendanceStatusEnum.Overtime:
        return "overtime";
      default:
        return "unknown";
    }
  };

  // Load departments
  const loadDepartments = async () => {
    try {
      setLoading((prev) => ({ ...prev, departments: true }));
      const departments = await service.getDepartments();

      setAttendanceData((prev) => ({
        ...prev,
        departments: Array.isArray(departments) ? departments : [],
      }));

      // Auto-select first department AND load its data
      if (departments.length > 0 && !selectedDepartment) {
        const firstDeptId = departments[0].id;
        setSelectedDepartment(firstDeptId);

        // Load data for the first department
        const today = dayjs();
        const startOfWeek = today.startOf("week").format("YYYY-MM-DD");
        const endOfWeek = today.endOf("week").format("YYYY-MM-DD");
        loadDepartmentAttendance(firstDeptId, 0, startOfWeek, endOfWeek);
      }
    } catch (err) {
      console.error("Error loading departments:", err);
    } finally {
      setLoading((prev) => ({ ...prev, departments: false }));
    }
  };

  // Load Department Attendance
  const loadDepartmentAttendance = async (
    departmentId = selectedDepartment,
    viewType = 0,
    startDate,
    endDate
  ) => {
    try {
      if (!departmentId) return;

      setLoading((prev) => ({ ...prev, department: true }));
      // Fetch attendance data from service
      const response = await service.getDepartmentAttendance(
        departmentId,
        viewType,
        startDate,
        endDate
      );
      // Get department name for mapping
      const currentDept = attendanceData.departments.find((dept) => dept.id === departmentId);
      const deptName = currentDept?.name || "General";

      // Transform API data to match expected structure
      const records =
        response.records?.map((record) => ({
          date: record.date,
          isWeekend: record.isWeekend,
          isHoliday: record.isHoliday,
          totalPresent: record.totalPresent,
          totalAbsent: record.totalAbsent,
          totalOnLeave: record.totalOnLeave,
          totalLate: record.totalLate,
          // Map 'employees' to 'users' as expected by the component
          users:
            record.employees?.map((user) => ({
              id: user.employeeId,
              userId: user.employeeId,
              name: user.fullName,
              department: deptName,
              status: getStatusFromCode(user.status),
              statusCode: user.status,
              statusLabel: AttendenceStatusLabel[user.status],
              isLate: user.isLate,
              isEarlyLeave: user.isEarlyLeave,
              totalWorkedHours: user.totalWorkedHours,
              date: user.date,
            })) || [],
        })) || [];

      // ------------------- Charts Data -------------------
      const labels = records.map((r) => r.date); // X-axis: dates

      setBarChartData({
        labels,
        datasets: [
          {
            label: "Present",
            data: records.map((r) => r.totalPresent || 0),
            backgroundColor: "#4CAF50",
          },
          {
            label: "Absent",
            data: records.map((r) => r.totalAbsent || 0),
            backgroundColor: "#F44336",
          },
          {
            label: "On Leave",
            data: records.map((r) => r.totalOnLeave || 0),
            backgroundColor: "#FFC107",
          },
          { label: "Late", data: records.map((r) => r.totalLate || 0), backgroundColor: "#2196F3" },
        ],
      });

      setPieChartData({
        labels: ["Present", "Absent", "On Leave", "Late"],
        datasets: [
          {
            data: [
              records.reduce((sum, r) => sum + (r.totalPresent || 0), 0),
              records.reduce((sum, r) => sum + (r.totalAbsent || 0), 0),
              records.reduce((sum, r) => sum + (r.totalOnLeave || 0), 0),
              records.reduce((sum, r) => sum + (r.totalLate || 0), 0),
            ],
            backgroundColor: ["#4CAF50", "#F44336", "#FFC107", "#2196F3"],
            borderWidth: 1,
          },
        ],
      });
      // ------------------- End Charts -------------------

      // Extract unique employees
      const employeeMap = new Map();
      records.forEach((record) => {
        record.users?.forEach((user) => {
          if (user.userId && !employeeMap.has(user.userId)) {
            employeeMap.set(user.userId, {
              id: user.userId,
              name: user.name,
              department: deptName,
            });
          }
        });
      });
      const employees = Array.from(employeeMap.values());

      // Summary for today
      const today = dayjs().format("YYYY-MM-DD");
      const todayRecord = records.find((r) => r.date === today);

      const summary = {
        totalEmployees: employees.length,
        presentToday: todayRecord?.totalPresent || 0,
        absentToday: todayRecord?.totalAbsent || 0,
        onLeave: todayRecord?.totalOnLeave || 0,
        lateArrivals: todayRecord?.totalLate || 0,
      };

      setAttendanceData((prev) => ({
        ...prev,
        records,
        summary,
        employees,
      }));
    } catch (err) {
      console.error("Error loading department attendance:", err);
    } finally {
      setLoading((prev) => ({ ...prev, department: false }));
    }
  };

  // Get attendance for specific date
  const getAttendanceByDate = (date) => {
    const dateString = dayjs(date).format("YYYY-MM-DD");
    return attendanceData.records.find((record) => record.date === dateString) || null;
  };

  const getTodayEmployees = () => {
    // First try to get today's date
    const today = dayjs().format("YYYY-MM-DD");
    let todayRecord = attendanceData.records.find((record) => record.date === today);

    // If today's date isn't in the records, get the most recent date
    if (!todayRecord && attendanceData.records.length > 0) {
      // Sort records by date descending and get the first (most recent)
      const sortedRecords = [...attendanceData.records].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      todayRecord = sortedRecords[0];
    }

    if (todayRecord?.users) {
      return todayRecord.users.map((user) => ({
        id: user.userId,
        name: user.name,
        department: user.department || "General",
        status: user.status,
        statusCode: user.statusCode,
        statusLabel: user.statusLabel,
        checkInTime: user.totalWorkedHours !== "00:00:00" ? "09:00 AM" : null,
        hoursWorked: formatWorkedHours(user.totalWorkedHours),
        totalWorkedHours: user.totalWorkedHours,
        isLate: user.isLate,
        isEarlyLeave: user.isEarlyLeave,
      }));
    }

    return [];
  };

  // Format worked hours
  const formatWorkedHours = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "0h";
    try {
      const [hours, minutes] = timeString.split(":");
      const h = parseInt(hours),
        m = parseInt(minutes);
      if (h === 0 && m === 0) return "0h";
      if (h === 0) return `${m}m`;
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    } catch {
      return "0h";
    }
  };

  // Handle employee selection
  const handleEmployeeSelect = (employeeId) => {
    const employee = attendanceData.employees.find((emp) => emp.id === employeeId);
    setSelectedEmployee(employee || null);
  };

  // Handle department selection
  const handleDepartmentSelect = (departmentId) => {
    setSelectedDepartment(departmentId);
    setSelectedEmployee(null);
    if (departmentId) {
      loadDepartmentAttendance(departmentId);
    }
  };

  // Initialize data
  useEffect(() => {
    loadDepartments();
  }, []);

  // Load data when department changes
  useEffect(() => {
    if (selectedDepartment) {
      // Get today's date
      const today = dayjs();
      // Calculate the start of the week (Sunday)
      const startOfWeek = today.startOf("week").format("YYYY-MM-DD");
      // Calculate the end of the week (Saturday)
      const endOfWeek = today.endOf("week").format("YYYY-MM-DD");

      // Use viewType 0 for week view with current week's dates
      loadDepartmentAttendance(selectedDepartment, 0, startOfWeek, endOfWeek);
    }
  }, [selectedDepartment]);

  return {
    attendanceData,
    loading,
    selectedDepartment,
    selectedEmployee,
    currentDate,
    setSelectedDepartment: handleDepartmentSelect,
    setCurrentDate,
    setSelectedEmployee: handleEmployeeSelect,
    getAttendanceByDate,
    getTodayEmployees,
    handleEmployeeSelect,
    loadDepartmentAttendance,
    barChartData,
    pieChartData,
    options,
  };
};

export default useAttendance;
